import React, { useState, useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { FiEdit, FiRefreshCw } from 'react-icons/fi';
import { RiDeleteBinLine } from "react-icons/ri";
import { Checkbox } from '~components/forms/checkbox';
import { UserContext } from '~contexts/user';

import { useStateFromProp } from '~hooks/common';
import Modal from '~components/modal';
import Button from '~components/button';
import Loader from '~components/loader';
import './style.scss';


const ActionsModal = ({ isOpened, isLoading, onToggle = () => {}, onConfirm, actionType }) => {
  const intl = useIntl();
  const [isModalOpened, setModalOpen] = useStateFromProp(isOpened, onToggle);

  const handleConfirm = () => {
    onConfirm(); 
    // setModalOpen(false);
  };

  return (
    <Modal.Wrapper isOpened={isModalOpened} onClose={() => setModalOpen(false)} className="">
      <Modal.Header>
        <h3 className="l-headline--tertiary">
          {actionType === 'resume' 
            ? intl.formatMessage({
                id: 'containers.account.subscription.modal.headline.resume_subscription',
                defaultMessage: 'Resume Subscription',
              })
            : actionType === 'pause' 
            ? intl.formatMessage({
                id: 'containers.account.subscription.modal.headline.pause_subscription',
                defaultMessage: 'Pause Subscription',
              })
            : intl.formatMessage({
                id: 'containers.account.subscription.modal.headline.delete_subscription',
                defaultMessage: 'Delete Subscription',
              })
          }
        </h3>
      </Modal.Header>

      <Modal.Content>
        {actionType === 'resume'
          ? intl.formatMessage({
              id: 'containers.account.subscription.modal.content.resume_subscription',
              defaultMessage: 'Are you sure you want to resume your subscription?',
            })
          : actionType === 'pause'
          ? intl.formatMessage({
              id: 'containers.account.subscription.modal.content.pause_subscription',
              defaultMessage: 'Are you sure you want to pause your subscription?',
            })
          : intl.formatMessage({
              id: 'containers.account.subscription.modal.content.delete_subscription',
              defaultMessage: 'Are you sure you want to delete your subscription? This action cannot be undone.',
            })
        }
      </Modal.Content>


      <Modal.Footer>
        <div className="c-modal__buttons--flex-end">
          <Button className="c-modal__button c-btn--text" onClick={() => setModalOpen(false)}>
            cancel
          </Button>
          <Button className="c-modal__button c-btn" onClick={handleConfirm} isLoading={isLoading} disabled={isLoading}>
            save
          </Button>
        </div>
      </Modal.Footer>
    </Modal.Wrapper>
  );
};



const Subscription = () => {
  const intl = useIntl();

  const [isModalOpened, setIsModalOpened] = useState(false);
  const { user, getSubscriptionPayments, resumeSubscription, pauseSubscription, deleteSubscription} = useContext(UserContext);
  const { personId, accountType, subscriptions } = user;
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionPayments, setSubscriptionPayments] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [checkedValue, setCheckedValue] = useState(null);
  const [actionType, setActionType] = useState(null); 
  const [confirmActionFlag, setConfirmActionFlag] = useState(false);
  const [subscriptionIdForAction, setSubscriptionIdForAction] = useState(null);


  const handleChange = (subscription_id, value) => {
    setCheckedValue(value); 
    setActionType(value ? 'resume' : 'pause'); 
    setIsModalOpened(true);
    setSubscriptionIdForAction(subscription_id);
  };

  const handleDelete = (subscription_id) => {
    setActionType('delete'); 
    setIsModalOpened(true);
    setSubscriptionIdForAction(subscription_id);
  };

  const confirmAction = async (actionType, subscriptionId) => {
    setIsSaving(true);
    // setIsLoading(true);
    try {
      if (actionType === 'resume') {
        await resumeSubscription(subscriptionId);
      } else if(actionType === 'pause') {
        await pauseSubscription(subscriptionId); 
      }else if(actionType === 'delete') {
        await deleteSubscription(subscriptionId); 
      }
      setConfirmActionFlag(prev => !prev); 
    } catch (err) {
      console.log('Error while updating subscription', err);
    } finally {
      setIsSaving(false);
      setIsModalOpened(false); 
      // setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchSubscriptionPaymentsFunc = async () => {
      setIsLoading(true);
      try {
        const fetchedSubscriptionPayments = await getSubscriptionPayments(personId);
        setSubscriptionPayments(fetchedSubscriptionPayments || []);
      } catch (err) {
        setSubscriptionPayments([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriptionPaymentsFunc();
  }, [personId, confirmActionFlag]);

  return (
    <div className="c-box">
      <div className="c-box__content">
        <div className="c-editable">
          <div className="c-editable__content ">
            <h3 className="l-headline--tertiary c-box__headline">
              {intl.formatMessage({
                id: 'containers.account.subscription.headline',
                defaultMessage: 'Chat subscription',
              })}
            </h3>
            <div className="c-account-subscribe">
            
            {!isLoading ? (
              <>
                {subscriptionPayments.length > 0 ? (
                  subscriptionPayments.map((payment) => (
                    payment.currentStatus === 'SUCCEEDED' ? (
                      <div className="c-checkbox c-checkbox--with-children" key={payment.paymentId}>
                        <Checkbox
                          checked={payment.subscriptions && payment.subscriptions.length > 0 && payment.subscriptions.some(sub => 
                            sub.subscriptionStatus === 'ONGOING' && sub.cancelAtPeriodEnd === false
                          )} // Only check if ONGOING and cancelAtPeriodEnd is false
                          onChange={(value) => {handleChange(payment.subscriptionId, value)}}
                          className="c-checkbox--with-children"
                          disabled={isSaving}
                        >
                          <div className="c-checkbox__children">
                            <div className="c-checkbox__title">
                            {payment.location ? `${payment.location.address}` : 'Address not found'} 
                            {payment.subscriptions && payment.subscriptions.length > 0 
                            ? (() => {
                                const ongoingSubscription = payment.subscriptions.find(sub => sub.subscriptionStatus === 'ONGOING');
                                return ongoingSubscription 
                                  ? ` (${ongoingSubscription.remainingConnects}/${ongoingSubscription.totalConnects})`
                                  : intl.formatMessage({
                                    id: 'containers.account.subscription.location.not_active',
                                    defaultMessage: 'Subscription is not active',
                                  })
                              })()
                            : intl.formatMessage({
                              id: 'containers.account.subscription.location.not_active',
                              defaultMessage: 'Subscription is not active',
                            })}
                            </div>
                            <div className="c-checkbox__content">

                            {payment.subscriptions && payment.subscriptions.length > 0 
                              ? (() => {
                                  const checkExpiry = payment.subscriptions.some(sub => sub.subscriptionStatus === 'ONGOING' && sub.cancelAtPeriodEnd === false);
                                  return checkExpiry 
                                    ? (
                                        <>
                                          {intl.formatMessage({
                                            id: 'containers.account.subscription.location.content',
                                            defaultMessage: 'Auto renew the chat subscription for this location at',
                                          })} {payment.nextRecurringDate ? new Date(payment.nextRecurringDate * 1000).toLocaleDateString() 
                                            : intl.formatMessage({
                                                id: 'containers.account.subscription.location.next_recurring_date_not_found',
                                                defaultMessage: 'Next Recurring Date Not Found',
                                              })}
                                        </>
                                      ) : (
                                        <>
                                          {intl.formatMessage({
                                            id: 'containers.account.subscription.location.expire',
                                            defaultMessage: 'Subscription will end for this location at',
                                          })} {payment.nextRecurringDate ? new Date(payment.nextRecurringDate * 1000).toLocaleDateString() 
                                            : intl.formatMessage({
                                                id: 'containers.account.subscription.location.next_recurring_date_not_found',
                                                defaultMessage: 'Next Recurring Date Not Found',
                                              })}
                                        </>
                                      );
                                })()
                              : intl.formatMessage({
                                  id: 'containers.account.subscription.location.not_active',
                                  defaultMessage: '( Subscription is not active )',
                                })
                            }
                            </div>                  
                          </div>
                        </Checkbox>
                      </div>
                    ) : payment.currentStatus === 'PROCESSING' ? (
                      <div className="c-checkbox c-checkbox--with-children" key={payment.paymentId}>
                        <div className="c-checkbox__children processing-payment">
                          <div className="c-checkbox__title">
                            {payment.location ? `${payment.location.address}` : 'Address not found'} 
                          </div>
                          <div className="c-checkbox__content">
                            {intl.formatMessage({
                              id: 'containers.account.subscription.location.processing',
                              defaultMessage: '( Payment is under processing, please refresh and keep an eye. )',
                            })}
                            <FiRefreshCw 
                              className="refresh-icon" 
                              onClick={() => setConfirmActionFlag(prev => !prev)}
                              style={{ 
                                marginLeft: '8px', 
                                cursor: 'pointer',
                                verticalAlign: 'middle'
                              }} 
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="c-checkbox c-checkbox--with-children" key={payment.paymentId}>
                        <Checkbox
                          checked={false}
                          onChange={(value) => {handleChange(payment.subscriptionId, value)}}
                          className="c-checkbox--with-children"
                          disabled={isSaving}
                        >
                          <div className="c-checkbox__children subscrib__pause">
                            <div className="c-checkbox__title">
                              {payment.location ? `${payment.location.address}` : 'Address not found'} 
                              {payment.subscriptions && payment.subscriptions.length > 0 
                              ? (() => {
                                  const ongoingSubscription = payment.subscriptions.find(sub => sub.subscriptionStatus === 'ONGOING');
                                  return ongoingSubscription 
                                    ? ` (${ongoingSubscription.remainingConnects}/${ongoingSubscription.totalConnects})`
                                    : intl.formatMessage({
                                      id: 'containers.account.subscription.location.not_active',
                                      defaultMessage: '( Subscription is not active )',
                                    })
                                })()
                              : intl.formatMessage({
                                id: 'containers.account.subscription.location.not_active',
                                defaultMessage: '( Subscription is not active )',
                              })}
                            </div>                    
                          </div>
                          
                        </Checkbox>
                          <button type="button" className="c-editable__button del-btn" aria-label={intl.formatMessage({ id: 'common.delete', defaultMessage: 'Delete' })} onClick={() => {handleDelete(payment.subscriptionId)}}><RiDeleteBinLine aria-hidden="true" /></button>
                          <Link to="/edit-wish" className="c-editable__button" data-cy="c-client-offer-editable__button"><FiEdit/></Link>
                      </div>
                    )
                  ))
                ) : (
                  <div>
                    {
                      intl.formatMessage({
                        id: 'containers.account.subscription.not_found',
                        defaultMessage: 'No Subscription Found',
                      })
                    }
                  </div> 
                )}
              </>
            ):(
              <Loader />
            )}
              
            </div>
          </div>
        </div>
      </div>
      <ActionsModal 
        isOpened={isModalOpened} 
        isLoading={isSaving}
        onToggle={() => setIsModalOpened(false)} 
        onConfirm={() => confirmAction(actionType, subscriptionIdForAction)}
        actionType={actionType}
      />
    </div>
  );
};

export default Subscription;
